import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, latestVideos, featuredVideos } from '@/mocks/videos';
import { siteInfo } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSort, setActiveSort] = useState<'latest' | 'popular'>('latest');

  const category = useMemo(() => categories.find((c) => c.slug === slug), [slug]);

  const allVideos = useMemo(() => {
    const combined = [...featuredVideos, ...latestVideos];
    const filtered = combined.filter((v) => v.categorySlug === slug);
    if (activeSort === 'popular') {
      return [...filtered].sort((a, b) => {
        const numA = parseFloat(a.views.replace('万', ''));
        const numB = parseFloat(b.views.replace('万', ''));
        return numB - numA;
      });
    }
    return filtered;
  }, [slug, activeSort]);

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="pt-24 pb-16 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-950">分类未找到</h1>
          <Link to="/" className="mt-4 inline-block text-primary-600 hover:text-primary-700 cursor-pointer">返回首页</Link>
        </div>
        <Footer />
      </>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - ${siteInfo.name}`,
    description: `${siteInfo.name} ${category.name}分类，共${category.count}个精彩视频`,
    url: `${siteInfo.url}/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: siteInfo.url,
    },
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <Navbar />

      <main className="pt-20">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="px-4 md:px-6 py-8 md:py-12">
            <div className="max-w-7xl mx-auto">
              <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-4">
                <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line"></i>
                </span>
                <span className="text-foreground-800 font-medium">{category.name}</span>
              </nav>

              <div className="flex items-center gap-4">
                <span className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className={`${category.icon} text-2xl md:text-3xl`}></i>
                </span>
                <div>
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                    {category.name}
                  </h1>
                  <p className="mt-1 text-sm text-foreground-500">
                    共 {category.count} 个视频
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="px-4 md:px-6 py-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-4">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      cat.slug === slug
                        ? 'text-primary-600 border-b-2 border-primary-500 pb-1'
                        : 'text-foreground-500 hover:text-foreground-800'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>

              <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
                <button
                  onClick={() => setActiveSort('latest')}
                  className={`px-4 py-1.5 text-xs font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                    activeSort === 'latest'
                      ? 'bg-background-50 text-foreground-950'
                      : 'text-foreground-500 hover:text-foreground-800'
                  }`}
                >
                  最新
                </button>
                <button
                  onClick={() => setActiveSort('popular')}
                  className={`px-4 py-1.5 text-xs font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                    activeSort === 'popular'
                      ? 'bg-background-50 text-foreground-950'
                      : 'text-foreground-500 hover:text-foreground-800'
                  }`}
                >
                  最热
                </button>
              </div>
            </div>

            {allVideos.length === 0 ? (
              <div className="text-center py-16">
                <span className="w-16 h-16 flex items-center justify-center mx-auto rounded-full bg-background-100 text-foreground-300 mb-4">
                  <i className="ri-video-line text-3xl"></i>
                </span>
                <p className="text-foreground-500">该分类暂无视频内容</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {allVideos.map((video, idx) => (
                  <Link
                    key={video.id}
                    to={`/article/${video.slug}`}
                    className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden transition-all duration-300 hover:scale-[1.02] cursor-pointer"
                  >
                    <div className="relative aspect-video overflow-hidden">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <span className="absolute bottom-2 right-2 px-2 py-0.5 bg-black/70 text-background-50 text-xs rounded-md font-medium">
                        {video.duration}
                      </span>
                      {idx === 0 && activeSort === 'latest' && (
                        <span className="absolute top-3 left-3 px-2.5 py-1 bg-accent-500 text-background-50 text-xs rounded-full font-medium">
                          最新
                        </span>
                      )}
                      {idx === 0 && activeSort === 'popular' && (
                        <span className="absolute top-3 left-3 px-2.5 py-1 bg-primary-500 text-background-50 text-xs rounded-full font-medium">
                          最热
                        </span>
                      )}
                    </div>
                    <div className="p-4 md:p-5">
                      <h3 className="text-base font-heading font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
                        {video.title}
                      </h3>
                      <p className="mt-2 text-sm text-foreground-500 line-clamp-2 leading-relaxed">
                        {video.excerpt}
                      </p>
                      <div className="flex items-center gap-4 mt-4 text-xs text-foreground-400">
                        <span className="flex items-center gap-1">
                          <span className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-eye-line"></i>
                          </span>
                          {video.views}
                        </span>
                        <span>{video.date}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}